<template>
  <SvoiDraggable v-model="originList" class="svoi-files-list">
    <template #item="{ element: file, id }">
      <div :key="id" class="file">
        <div class="preview">{{ getFileType(file) }}</div>
        <div class="metadata">
          <a v-if="file.path" :href="file.path" target="_blank" class="name-link">{{ file.name }}</a>
          <span v-else class="name">{{ file.name }}</span>
          <!-- eslint-disable-next-line vue/no-v-html -->
          <span class="info" v-html="getFileMetadata(file)" />
        </div>
        <SvoiIcon
          v-if="!disabled"
          class="remove-icon"
          icon="essential/xmark"
          size="s"
          @click="onClickDeleteFile(file)"
        />
      </div>
    </template>
  </SvoiDraggable>
</template>

<script lang="ts">
import type { ComponentPropsOptions, PropType } from 'vue-demi'
import { computed, defineComponent, isVue2 } from 'vue-demi'
import { SvoiIcon } from '@svoi-ui/components/icon'
import type { FileData } from '@svoi-ui/interfaces/file-picker'
import { isFile } from '@svoi-ui/shared/utils/guards'
import { uuid } from '@svoi-ui/shared/utils/helpers'
import { SvoiDraggable } from './'

const modelProp: ComponentPropsOptions = isVue2
  ? {
      value: {
        type: Array as PropType<Array<File | FileData>>,
        default: [] as Array<File | FileData>
      }
    }
  : {
      modelValue: {
        type: Array as PropType<Array<File | FileData>>,
        default: [] as Array<File | FileData>
      }
    }

export default defineComponent({
  name: 'SvoiFilesList',
  components: { SvoiDraggable, SvoiIcon },
  props: {
    ...modelProp,
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['input', 'on-delete-file', 'update:modelValue'],
  setup(props, { emit }) {
    const previewFileSize = (size: number): string => {
      let res = size
      let unit = 'Б'

      if (res > 1024) {
        res = res / 1024
        unit = 'Кб'
      }

      if (res > 1024) {
        res = res / 1024
        unit = 'Мб'
      }

      return `${res.toFixed(1)} ${unit}`
    }

    const getFileMetadata = (file: FileData): string => {
      const chunks: Array<string> = []

      if (!file.origin) {
        chunks.push('<span class="status">Загружено</span>')
      }

      if (file.updatedAt) {
        chunks.push(new Date(file.updatedAt).toLocaleString())
      }

      if (file.size) {
        chunks.push(previewFileSize(file.size))
      }

      return chunks.join(' • ')
    }

    const getFileType = (file: FileData): string => {
      if (file.mimeType) {
        const [type, subtype] = file.mimeType.split('/')

        if (type === 'image') {
          return subtype.slice(0, 4).toUpperCase()
        }

        if (subtype === 'vnd.openxmlformats-officedocument.spreadsheetml.sheet' || subtype === 'vnd.ms-excel') {
          return 'XLS'
        }

        if (subtype === 'vnd.openxmlformats-officedocument.wordprocessingml.document' || subtype === 'msword') {
          return 'DOC'
        }

        if (subtype && subtype.length <= 4) {
          return subtype.toUpperCase()
        }
      }

      return '...'
    }

    const onClickDeleteFile = (file: FileData) => {
      if (!props.disabled) {
        emit('on-delete-file', file.origin ?? file)
      }
    }

    const convertToFileData = (file: File): FileData => {
      return {
        id: uuid(),
        name: file.name,
        mimeType: file.type,
        size: file.size,
        updatedAt: new Date(file.lastModified),
        origin: file
      }
    }

    // @ts-expect-error
    const propValue = computed<Array<File | FileData>>(() => (isVue2 ? props.value : props.modelValue))
    const originList = computed({
      get: () => propValue.value,
      set: val => {
        if (!props.disabled) {
          emit(isVue2 ? 'input' : 'update:modelValue', val)
        }
      }
    })
    const list = computed(() => propValue.value.map(i => (isFile(i) ? convertToFileData(i) : i)))

    return {
      list,
      originList,
      getFileMetadata,
      getFileType,
      onClickDeleteFile
    }
  }
})
</script>

<style lang="scss" scoped>
@use '@svoi-ui/assets/scss/components/list.scss';
</style>
